<template>
  <div class="profile-container">
    <!-- Loading State -->
    <div v-if="get_loading" class="loading-section">
      <div class="modern-loading">
        <div class="loading-avatar"></div>
        <div class="loading-content">
          <div class="loading-line long"></div>
          <div class="loading-line medium"></div>
          <div class="loading-line short"></div>
        </div>
      </div>
    </div>

    <!-- Profile Content -->
    <div v-if="!get_loading" class="profile-content">
      <!-- Profile Header -->
      <div class="profile-header">
        <div class="profile-background">
          <div class="background-pattern"></div>
        </div>
        
        <div class="profile-avatar-section">
          <div class="avatar-container">
            <div class="avatar-wrapper">
              <img 
                :src="getAvatarSrc(get_user.jenisKelamin)" 
                :alt="get_user.nama"
                class="profile-avatar"
              >
              <div class="avatar-ring"></div>
              <div class="avatar-status" :class="getGenderStatusClass(get_user.jenisKelamin)"></div>
            </div>
          </div>
          
          <div class="profile-basic-info">
            <h1 class="profile-name">{{ get_user.nama }}</h1>
            <p class="profile-nip">{{ formatNIP(get_user.nip) }}</p>
            <div class="profile-badges">
              <span class="badge primary">{{ get_user.jenisKelamin === 'L' ? 'Male' : get_user.jenisKelamin === 'P' ? 'Female' : 'Unknown' }}</span>
              <span class="badge secondary">Active</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Details -->
      <div class="profile-details">
        <div class="details-grid">
          <!-- Position Info -->
          <div class="detail-card">
            <div class="card-icon position-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Jabatan</h3>
              <p class="card-value">{{ get_user.jabatan || 'Tidak ada informasi' }}</p>
              <div class="card-accent"></div>
            </div>
          </div>

          <!-- Unit Work Info -->
          <div class="detail-card">
            <div class="card-icon unit-icon">
              <i class="el-icon-office-building"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Unit Kerja</h3>
              <p class="card-value">{{ get_user.unitKerja || 'Tidak ada informasi' }}</p>
              <div class="card-accent"></div>
            </div>
          </div>

          <!-- Additional Info -->
          <div class="detail-card">
            <div class="card-icon info-icon">
              <i class="el-icon-info"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Status</h3>
              <p class="card-value">Pegawai Aktif</p>
              <div class="card-accent"></div>
            </div>
          </div>

          <!-- Contact Info -->
          <div class="detail-card">
            <div class="card-icon contact-icon">
              <i class="el-icon-message"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Email</h3>
              <p class="card-value">{{ get_user.username || get_user.nip }}@lan.go.id</p>
              <div class="card-accent"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="profile-actions">
        <button class="action-btn primary-btn" @click="open_ganti_password">
          <i class="el-icon-lock"></i>
          <span>Ganti Password</span>
        </button>
        <button class="action-btn warning-btn" @click="open_update_pin">
          <i class="el-icon-edit"></i>
          <span>Update PIN</span>
        </button>
        <button class="action-btn danger-btn" @click="open_reset_pin">
          <i class="el-icon-refresh-right"></i>
          <span>Reset PIN</span>
        </button>
      </div>
    </div>

    <!-- Dialog Update PIN -->
    <el-dialog title="Update PIN" :visible.sync="dialogUpdatePin" width="450px">
      <p style="margin-bottom: 20px; color: #606266; font-size: 14px;">
        <i class="el-icon-info" style="color: #E6A23C;"></i>
        Masukkan PIN lama dan PIN baru untuk mengupdate PIN Anda.
      </p>
      <el-form :model="formUpdatePin" :rules="updatePinRules" ref="formUpdatePinRef" label-position="top">
        <el-form-item label="PIN Lama (4 digit)" prop="oldPin">
          <el-input 
            v-model="formUpdatePin.oldPin" 
            maxlength="4" 
            placeholder="Masukkan PIN lama" 
            type="password"
            show-password
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="PIN Baru (4 digit)" prop="newPin">
          <el-input 
            v-model="formUpdatePin.newPin" 
            maxlength="4" 
            placeholder="Masukkan 4 digit PIN baru" 
            type="password"
            show-password
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="Konfirmasi PIN Baru" prop="confirmPin">
          <el-input 
            v-model="formUpdatePin.confirmPin" 
            maxlength="4" 
            placeholder="Ulangi PIN baru" 
            type="password"
            show-password
            autocomplete="off"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="cancel_update_pin">Batal</el-button>
        <el-button type="warning" size="small" @click="submit_update_pin" :loading="loadingUpdatePin">
          Update PIN
        </el-button>
      </span>
    </el-dialog>

    <!-- Dialog Reset PIN -->
    <el-dialog title="Reset PIN" :visible.sync="dialogResetPin" width="450px">
      <p style="margin-bottom: 20px; color: #606266; font-size: 14px;">
        <i class="el-icon-info" style="color: #409EFF;"></i>
        Masukkan PIN baru untuk akun Anda. Sistem akan otomatis membuat atau mereset PIN Anda.
      </p>
      <el-form :model="formPin" :rules="pinRules" ref="formPinRef" label-position="top">
        <el-form-item label="PIN Baru (4 digit)" prop="newPin">
          <el-input 
            v-model="formPin.newPin" 
            maxlength="4" 
            placeholder="Masukkan 4 digit PIN baru" 
            type="password"
            show-password
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="Konfirmasi PIN" prop="confirmPin">
          <el-input 
            v-model="formPin.confirmPin" 
            maxlength="4" 
            placeholder="Ulangi PIN baru" 
            type="password"
            show-password
            autocomplete="off"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="cancel_reset_pin">Batal</el-button>
        <el-button type="danger" size="small" @click="submit_reset_pin" :loading="loadingPin">
          Reset PIN
        </el-button>
      </span>
    </el-dialog>
    <!-- <el-dialog title="Ganti Password" :visible.sync="dialogvisible" width="20%">
      <div>
        <div>
          <el-input class="roboto-mono-200" placeholder="Password" prefix-icon="el-icon-lock" v-model="form.newPassword" @change="validation_newPassword" show-password></el-input>
        </div>
        <div class="mt-2">
          <el-input class="roboto-mono-200" placeholder="Password" prefix-icon="el-icon-lock" v-model="form.confirmPassword" show-password></el-input>
        </div>
        <div>
          {{ message }}
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="cancel_ganti_password">Cancel</el-button>
        <el-button type="primary" size="mini" @click="submit_ganti_password">Confirm</el-button>
      </span>
    </el-dialog> -->
  </div>
</template>
,<script>
export default {
  data() {
    return {
      dialogvisible: false,
      dialogResetPin: false,
      dialogUpdatePin: false,
      loadingPin: false,
      loadingUpdatePin: false,
      form: {password: null, konfirmasi_password: null, newPassword: null, confirmPassword: null},
      formPin: { newPin: '', confirmPin: '' },
      formUpdatePin: { oldPin: '', newPin: '', confirmPin: '' },
      message: null
    }
  },
  computed: {
    get_user() {
      return this.$store.state.user.user
    },
    get_loading() {
      return this.$store.state.user.loading
    },
    pinRules() {
      return {
        newPin: [
          { required: true, message: 'PIN wajib diisi', trigger: 'blur' },
          { pattern: /^\d{4}$/, message: 'PIN harus 4 digit angka', trigger: 'blur' }
        ],
        confirmPin: [
          { required: true, message: 'Konfirmasi PIN wajib diisi', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              if (value !== this.formPin.newPin) {
                callback(new Error('Konfirmasi PIN tidak sama'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ]
      }
    },
    updatePinRules() {
      return {
        oldPin: [
          { required: true, message: 'PIN lama wajib diisi', trigger: 'blur' },
          { pattern: /^\d{4}$/, message: 'PIN harus 4 digit angka', trigger: 'blur' }
        ],
        newPin: [
          { required: true, message: 'PIN baru wajib diisi', trigger: 'blur' },
          { pattern: /^\d{4}$/, message: 'PIN harus 4 digit angka', trigger: 'blur' }
        ],
        confirmPin: [
          { required: true, message: 'Konfirmasi PIN wajib diisi', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              if (value !== this.formUpdatePin.newPin) {
                callback(new Error('Konfirmasi PIN tidak sama'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ]
      }
    },
    // validation_newPassword() {
    //   const newPassword = this.form.newPassword;

    //   // Password validation regex patterns
    //   const hasUpperCase = /[A-Z]/.test(newPassword);
    //   const hasLowerCase = /[a-z]/.test(newPassword);
    //   const hasNumbers = /\d/.test(newPassword);
    //   const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    //   const isValidLength = newPassword.length >= 8;

    //   // Check if the password meets all criteria
    //   if (!isValidLength || !hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChars) {
    //     this.message = 'Password must be at least 8 characters long and include uppercase letters, lowercase letters, numbers, and special characters.';
    //   }

    //   return this.message;
    // },
  },
  created() {
    this.$store.dispatch('user/get_user')
  },
  methods: {
    open_ganti_password: function() {
      this.dialogvisible = true;
      this.form.newPassword = null;
      this.form.confirmPassword = null;
      this.message = null;
    },
    cancel_ganti_password: function() {
      this.dialogvisible = false;
    },
    submit_ganti_password: function() {
      this.dialogvisible = false;
    },
    
    open_reset_pin() {
      this.dialogResetPin = true;
      this.formPin.newPin = '';
      this.formPin.confirmPin = '';
      // Reset form validation jika ada
      this.$nextTick(() => {
        if (this.$refs.formPinRef) {
          this.$refs.formPinRef.clearValidate();
        }
      });
    },
    
    cancel_reset_pin() {
      this.dialogResetPin = false;
      this.formPin.newPin = '';
      this.formPin.confirmPin = '';
    },

    open_update_pin() {
      this.dialogUpdatePin = true;
      this.formUpdatePin.oldPin = '';
      this.formUpdatePin.newPin = '';
      this.formUpdatePin.confirmPin = '';
      // Reset form validation
      this.$nextTick(() => {
        if (this.$refs.formUpdatePinRef) {
          this.$refs.formUpdatePinRef.clearValidate();
        }
      });
    },

    cancel_update_pin() {
      this.dialogUpdatePin = false;
      this.formUpdatePin.oldPin = '';
      this.formUpdatePin.newPin = '';
      this.formUpdatePin.confirmPin = '';
    },

    async submit_update_pin() {
      this.$refs.formUpdatePinRef.validate(async (valid) => {
        if (!valid) {
          this.$message.error('Mohon periksa kembali input Anda');
          return false;
        }

        this.loadingUpdatePin = true;

        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          
          console.log('🔄 Updating PIN...');
          
          // Call Update PIN API
          const updateResponse = await fetch(
            'https://apidev-intranet.lan.go.id/api/v2/auth/pin/update',
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                oldPin: this.formUpdatePin.oldPin,
                pin: this.formUpdatePin.newPin,
                confirmPin: this.formUpdatePin.confirmPin
              })
            }
          );

          const updateResult = await updateResponse.json();
          console.log('📥 Update PIN Response:', updateResult);

          if (updateResult.status === 200) {
            this.$message.success(updateResult.message || '✅ PIN berhasil diupdate!');
            this.dialogUpdatePin = false;
            this.formUpdatePin.oldPin = '';
            this.formUpdatePin.newPin = '';
            this.formUpdatePin.confirmPin = '';
          } else {
            // Handle error cases
            this.$message.error(updateResult.message || 'Gagal update PIN');
          }
        } catch (error) {
          console.error('❌ Error:', error);
          this.$message.error('Terjadi kesalahan: ' + error.message);
        } finally {
          this.loadingUpdatePin = false;
        }
      });
    },
    
    async submit_reset_pin() {
      this.$refs.formPinRef.validate(async (valid) => {
        if (!valid) {
          this.$message.error('Mohon periksa kembali input Anda');
          return false;
        }

        this.loadingPin = true;

        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          const username = this.get_user.username || this.get_user.nip;

          // Step 1: Coba Reset PIN
          console.log('🔄 Step 1: Mencoba reset PIN untuk user:', username);
          
          const resetResponse = await fetch(
            `https://apidev-intranet.lan.go.id/api/v2/auth/pin/reset/${username}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );

          const resetResult = await resetResponse.json();
          console.log('📥 Reset Response:', resetResult);

          // Jika user belum punya PIN (status 400)
          if (resetResult.status === 400 && resetResult.message === 'User belum memiliki PIN') {
            console.log('⚠️ User belum punya PIN, lanjut ke Set PIN...');
            
            // Step 2: Set PIN Awal
            const setPinResponse = await fetch(
              'https://apidev-intranet.lan.go.id/api/v2/auth/pin/set',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  pin: this.formPin.newPin,
                  confirmPin: this.formPin.confirmPin
                })
              }
            );

            const setPinResult = await setPinResponse.json();
            console.log('📥 Set PIN Response:', setPinResult);

            if (setPinResult.status === 200 && setPinResult.data?.setPin) {
              console.log('✅ PIN berhasil dibuat, lanjut verifikasi...');
              
              // Step 3: Verifikasi PIN yang baru dibuat
              const verifyResponse = await fetch(
                'https://apidev-intranet.lan.go.id/api/v2/auth/pin/verify',
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    pin: this.formPin.newPin
                  })
                }
              );

              const verifyResult = await verifyResponse.json();
              console.log('📥 Verify Response:', verifyResult);

              if (verifyResult.status === 200 && verifyResult.data?.pinVerified) {
                this.$message.success('✅ PIN berhasil dibuat dan diverifikasi!');
                this.dialogResetPin = false;
                this.formPin.newPin = '';
                this.formPin.confirmPin = '';
              } else {
                this.$message.error('Gagal verifikasi PIN: ' + verifyResult.message);
              }
            } else {
              this.$message.error('Gagal membuat PIN: ' + setPinResult.message);
            }
          } 
          // Jika user sudah punya PIN dan berhasil direset (status 200)
          else if (resetResult.status === 200 && resetResult.data?.pinReset) {
            console.log('✅ PIN berhasil direset');
            this.$message.success(resetResult.message || 'PIN berhasil direset!');
            this.dialogResetPin = false;
            this.formPin.newPin = '';
            this.formPin.confirmPin = '';
          } 
          // Error lainnya
          else {
            this.$message.error('Gagal memproses PIN: ' + resetResult.message);
          }
        } catch (error) {
          console.error('❌ Error:', error);
          this.$message.error('Terjadi kesalahan: ' + error.message);
        } finally {
          this.loadingPin = false;
        }
      });
    },
    
    getAvatarSrc(gender) {
      if (gender === 'L') return require('@/assets/img/user_p.png');
      if (gender === 'P') return require('@/assets/img/user_w.png');
      return require('@/assets/img/user.png');
    },
    
    getGenderStatusClass(gender) {
      if (gender === 'L') return 'male-status';
      if (gender === 'P') return 'female-status';
      return 'unknown-status';
    },
    
    formatNIP(nip) {
      if (!nip) return '';
      return nip.replace(/(\d{8})(\d{6})(\d{1})(\d{3})/, '$1 $2 $3 $4');
    },
    validation_password() {
      const newPassword = this.form.newPassword == null ? null : this.form.newPassword;
      const confirmPassword = this.form.confirmPassword == null ? null : this.form.confirmPassword;
      const message = null;

      // Password validation regex patterns
      const hasUpperCase = /[A-Z]/.test(newPassword);
      const hasLowerCase = /[a-z]/.test(newPassword);
      const hasNumbers = /\d/.test(newPassword);
      const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
      const isValidLength = newPassword.length >= 8;

      // Check if the password meets all criteria
      if (!isValidLength || !hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChars) {
        message= 'Password must be at least 8 characters long and include uppercase letters, lowercase letters, numbers, and special characters.';
        return;
      }

      // Check if passwords match
      if (newPassword === confirmPassword) {
        message = 'Password changed successfully!';
        // Here you can add code to handle the password change, e.g., send it to the server
      } else {
        message = 'Passwords do not match. Please try again.';
      }

      return message;
    }
  },
}
</script>

<style scoped>
/* Main Container */
.profile-container {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #f1f5f9 70%, #ffffff 100%);
  min-height: 100vh;
  padding: 2rem;
}

/* Loading State */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.modern-loading {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.loading-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(45deg, #60a5fa, #3b82f6, #60a5fa);
  background-size: 300% 300%;
  animation: shimmer 2s ease-in-out infinite;
}

.loading-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 200px;
}

.loading-line {
  height: 16px;
  background: linear-gradient(45deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 300% 300%;
  border-radius: 8px;
  animation: shimmer 2s ease-in-out infinite;
}

.loading-line.long { width: 100%; }
.loading-line.medium { width: 70%; }
.loading-line.short { width: 50%; }

@keyframes shimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

/* Profile Content */
.profile-content {
  max-width: 800px;
  margin: 0 auto;
  animation: fadeInUp 0.8s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Profile Header */
.profile-header {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
  overflow: hidden;
  margin-bottom: 2rem;
  position: relative;
}

.profile-background {
  height: 120px;
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  position: relative;
  overflow: hidden;
}

.background-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.2) 2px, transparent 2px),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 2px, transparent 2px),
    radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 50px 50px, 30px 30px, 20px 20px;
  animation: float 20s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateX(0px) translateY(0px); }
  50% { transform: translateX(10px) translateY(-10px); }
}

.profile-avatar-section {
  padding: 0 2rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-top: -60px;
}

.avatar-container {
  margin-bottom: 1.5rem;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.avatar-ring {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border: 3px solid transparent;
  border-radius: 50%;
  background: linear-gradient(45deg, #60a5fa, #3b82f6) border-box;
  mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: rotate 3s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.avatar-status {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 3;
}

.avatar-status.male-status {
  background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
}

.avatar-status.female-status {
  background: linear-gradient(135deg, #ea4335 0%, #fbbc04 100%);
}

.avatar-status.unknown-status {
  background: linear-gradient(135deg, #9aa0a6 0%, #5f6368 100%);
}

.profile-basic-info {
  text-align: center;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.profile-nip {
  font-size: 1.1rem;
  font-family: 'Courier New', monospace;
  color: #475569;
  margin: 0 0 1rem 0;
  background: rgba(59, 130, 246, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  display: inline-block;
}

.profile-badges {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge.primary {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.badge.secondary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

/* Profile Details */
.profile-details {
  margin-bottom: 2rem;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.detail-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: slideInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(20px);
}

.detail-card:nth-child(1) { animation-delay: 0.2s; }
.detail-card:nth-child(2) { animation-delay: 0.3s; }
.detail-card:nth-child(3) { animation-delay: 0.4s; }
.detail-card:nth-child(4) { animation-delay: 0.5s; }

@keyframes slideInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.detail-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.12);
}

.detail-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.05), transparent);
  transition: left 0.6s ease;
}

.detail-card:hover::before {
  left: 100%;
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.detail-card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
}

.position-icon {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
}

.unit-icon {
  background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
}

.info-icon {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
}

.contact-icon {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.card-content {
  position: relative;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 0.75rem 0;
}

.card-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  line-height: 1.4;
}

.card-accent {
  position: absolute;
  bottom: -1rem;
  right: -1rem;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  border-radius: 50%;
  opacity: 0.08;
  transition: all 0.3s ease;
}

.detail-card:hover .card-accent {
  opacity: 0.15;
  transform: scale(1.2);
}

/* Profile Actions */
.profile-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 160px;
  justify-content: center;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.action-btn:hover::before {
  left: 100%;
}

.primary-btn {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.95);
  color: #3b82f6;
  border: 2px solid rgba(59, 130, 246, 0.2);
  backdrop-filter: blur(20px);
}

.secondary-btn:hover {
  background: rgba(59, 130, 246, 0.08);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.2);
}

.warning-btn {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
}

.warning-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(245, 158, 11, 0.4);
}

.danger-btn {
  background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(231, 76, 60, 0.3);
}

.danger-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(231, 76, 60, 0.4);
}

.action-btn i {
  font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .profile-container {
    padding: 1rem;
  }
  
  .profile-header {
    border-radius: 20px;
    margin-bottom: 1.5rem;
  }
  
  .profile-avatar-section {
    padding: 0 1.5rem 1.5rem;
  }
  
  .profile-avatar {
    width: 100px;
    height: 100px;
  }
  
  .profile-name {
    font-size: 1.75rem;
  }
  
  .profile-nip {
    font-size: 1rem;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .detail-card {
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  .card-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }
  
  .profile-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 280px;
  }
}

@media (max-width: 480px) {
  .profile-container {
    padding: 0.5rem;
  }
  
  .profile-background {
    height: 80px;
  }
  
  .profile-avatar-section {
    margin-top: -40px;
    padding: 0 1rem 1rem;
  }
  
  .profile-avatar {
    width: 80px;
    height: 80px;
  }
  
  .avatar-status {
    width: 20px;
    height: 20px;
    bottom: 6px;
    right: 6px;
  }
  
  .profile-name {
    font-size: 1.5rem;
  }
  
  .profile-nip {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
  }
  
  .profile-badges {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .badge {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .detail-card {
    padding: 1.25rem;
  }
  
  .card-icon {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
  
  .card-title {
    font-size: 0.8rem;
  }
  
  .card-value {
    font-size: 1rem;
  }
  
  .action-btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
  }
}

/* Custom Scrollbar */
.profile-container::-webkit-scrollbar {
  width: 8px;
}

.profile-container::-webkit-scrollbar-track {
  background: rgba(148, 163, 184, 0.1);
  border-radius: 4px;
}

.profile-container::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.3);
  border-radius: 4px;
}

.profile-container::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}
</style>